/* @flow */
import * as React from "react";
import {
  withColorsProvider,
  type ColorSet,
} from "nx-components/ColorsProvider";
import Button from "nx-components/Button";
import prefixStyles from "nx-components/utils/prefixStyles";
import enqueueIn from "nx-components/utils/enqueueIn";
import ScrollableDiv from "nx-components/ScrollableDiv";
import Prompt from "nexia_mobile/components/PinEntry/Prompt";
import PivotKeyboard from "nexia_mobile/components/PinEntry/PivotKeyboard";
import PinLengthConfiguration from "nexia_mobile/components/PinEntry/PinLengthConfiguration";
import Theme from "nx-components/Theme";
import LanguageContext from "nx-components/ContextWrappers/LanguageContext";

const termsAndConditions = `http://tranecommercial.helpdocsonline.com/terms-conditions`;

type EULAProps = {
  colors: ColorSet,
};

class EULAContent extends React.Component<EULAProps> {
  render() {
    const { colors } = this.props;
    const language = this.context.localeSet.nexia_mobile.PinEntry
      .PivotPinEntryWrapper;

    const EULAStyles = prefixStyles({
      footerStyle: {
        backgroundImage: `linear-gradient(#aabbc2,${colors.PRIMARY_GRADIENT_DARK})`,
      },
      pinEntrySpan: {
        flex: "1 1 auto",
        display: "flex",
        fontSize: "0.70em",
        color: "white",
        flexDirection: "column",
        margin: "1.5em",
      },
    });
    return (
      <div style={EULAStyles.footerStyle}>
        <span style={EULAStyles.pinEntrySpan}>
          {language.EULApt1}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href={termsAndConditions}
          >
            {language.EULApt2Link}
          </a>
        </span>
        <span style={EULAStyles.pinEntrySpan}>
          ©2022 Trane | {language.privacyPolicy} | {language.termsOfUse}
        </span>
      </div>
    );
  }
}

const EULADiv = withColorsProvider(EULAContent);

const forgotPinStyles = prefixStyles({
  forgotPin: {
    fontSize: "0.6em",
    margin: "0.75em",
  },
});

type ForgotPinProps = {
  onForgotPin?: ?() => void,
};

class ForgotPin extends React.Component<ForgotPinProps> {
  render() {
    const { onForgotPin } = this.props;
    const language = this.context.localeSet.nexia_mobile.PinEntry
      .PivotPinEntryWrapper;

    return (
      <Button
        theme="link"
        style={forgotPinStyles.forgotPin}
        onTap={onForgotPin}
      >
        {language.forgotPinButton}
      </Button>
    );
  }
}

type Props = {
  prompt: string,
  length: number,
  password: boolean,
  disabled: boolean,
  showForgotPin: boolean,
  onChangeValue?: ?(newValue: string) => void,
  onDone: (value: string) => void,
  onForgotPin?: ?() => void,
  showEULA: boolean,
  canChangeLength: boolean,
  lengthChoices: Array<number>,
  onNewLengthRequested?: ?(number) => void,
};

type State = {
  value: string,
};

class PivotPinEntryWrapper extends React.Component<Props, State> {
  static defaultProps = {
    prompt: "Enter some numbers",
    length: 4,
    password: false,
    disabled: false,
    showForgotPin: false,
    showEULA: false,
    canChangeLength: false,
    lengthChoices: [],
  };

  state = {
    value: "",
  };

  reset = () => {
    this.setState({ value: "" });
  };

  _onKeypad = (key: string) => {
    const { disabled, onDone, onChangeValue } = this.props;
    let newValue;

    if (disabled) {
      return;
    }
    if (key === "backspace") {
      newValue = this.state.value.slice(0, -1);
    } else {
      newValue = this.state.value + key;
      if (newValue.length > this.props.length) {
        return; // don't update state, don't call back to parent
      }
    }

    this.setState({ value: newValue });
    if (onChangeValue) {
      onChangeValue(newValue);
    }
    if (newValue.length === this.props.length && onDone) {
      // Adding a 100 ms delay to make the last pin digit fill in
      enqueueIn(() => {
        onDone(newValue);
      }, 100);
    }
  };

  render() {
    const {
      disabled,
      prompt,
      length,
      showForgotPin,
      password,
      onForgotPin,
      showEULA,
      canChangeLength,
      lengthChoices,
      onNewLengthRequested,
    } = this.props;

    const { value } = this.state;

    const styles = prefixStyles({
      pinEntryVerticalContainer: {
        flex: "1 1 auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        overflow: "hidden",

        minHeight: "100%",
        // iPhone X has a "safe area" and it was covering the logo.
        paddingTop: Theme.Padding.SAFE_AREA_TOP,
        paddingBottom: Theme.Padding.SAFE_AREA_BOTTOM,
      },
      pinEntryWrapper: {
        flex: "1 1 auto",
        display: "flex",
        alignItems: "center",
        width: "100%",
        flexDirection: "column",
        justifyContent: "center",
        marginTop: "0.5em",
        alignSelf: "center",
      },
      forgotPin: {
        fontSize: "0.6em",
        margin: "0.75em",
        textTransform: "uppercase",
      },
    });

    return (
      <ScrollableDiv
        style={{
          flex: "1 1 100%",
          flexDirection: "column",
        }}
      >
        <div
          data-component-name="PivotPinEntryWrapper"
          style={styles.pinEntryVerticalContainer}
        >
          <div style={styles.pinEntryWrapper}>
            <Prompt
              isPortrait
              value={value}
              prompt={prompt}
              length={length}
              password={password}
              onForgotPin={onForgotPin}
            >
              {canChangeLength && (
                <PinLengthConfiguration
                  currentLength={length}
                  lengthChoices={lengthChoices}
                  onNewLengthRequested={onNewLengthRequested}
                />
              )}
            </Prompt>
            <PivotKeyboard onKeypad={this._onKeypad} disabled={disabled} />
            {showForgotPin ? <ForgotPin onForgotPin={onForgotPin} /> : null}
            {showEULA ? <EULADiv /> : null}
          </div>
        </div>
      </ScrollableDiv>
    );
  }
}

export default PivotPinEntryWrapper;

EULAContent.contextType = LanguageContext;
ForgotPin.contextType = LanguageContext;



// WEBPACK FOOTER //
// ./src/components/PinEntry/PivotPinEntryWrapper.jsx